/**
 * NavItem targetAnchor 三形态解析纯函数（F13，ADR-0014）。
 *
 * `targetAnchor` 四形态（issue Q1/Q5/Q3 + module page 决策）：
 *   - `#xxx`              → 首页内锚点平滑滚动（`xxx` = 区 type，如 `problem`）
 *   - `<guid>`            → `router.push('/detail/<title-slug>')` 跳详情页顶部
 *   - `<guid>#<anchor>`   → 跳详情页后滚动到该 Section bodyHtml 内同名元素
 *   - `module:<route>[/<moduleId>][#anchor]` → 数据库驱动模块页
 *   - 其他字符串           → 配置错误（invalid），可见错误提示 + console.error
 *
 * **Guid 识别**（用户确认 Q1）：用标准 .NET Guid wire 格式正则（hyphenated
 * 36 位 hex）严格区分 Guid 与「非法字符串」。后端 `System.Text.Json` 序列化
 * `Guid` 为小写 hyphenated 形态，`GetGuid()` 解析端已钉死。bare 非 Guid 字符串
 * 归 invalid 类，避免把任意配置笔误当成 sectionId 跳转到不存在的详情页。
 *
 * 纯函数 + readonly 入参，无 Vue / DOM / router 依赖，便于单测。
 */
import {
  isModuleId,
  isModuleRouteName,
  type ModuleRouteName,
} from '@/composables/module-target';

const HASH_PREFIX = '#';
const MODULE_PREFIX = 'module:';
const GUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * 同 Guid 相邻 NavItem 合并后聚焦 Material 区用的 anchor（F13 issue
 * 「合并为一个详情入口，点击后可聚焦 Material 区」）。DetailPageBody 给
 * `<MaterialList>` 根 `<section>` 挂 `id="materials"`，详情页 hash 滚动命中。
 */
export const MATERIALS_FOCUS_ANCHOR = 'materials';

export type NavTarget =
  | { readonly kind: 'home-anchor'; readonly id: string; readonly hash: string }
  | { readonly kind: 'detail'; readonly sectionId: string; readonly anchor?: string }
  | {
    readonly kind: 'module';
    readonly routeName: ModuleRouteName;
    readonly moduleId?: string;
    readonly anchor?: string;
  }
  | { readonly kind: 'invalid'; readonly raw: string };

/** 判断字符串是否为标准 hyphenated Guid wire 形态。 */
export function isGuidWire(value: string): boolean {
  return GUID_PATTERN.test(value);
}

/**
 * 解析 `targetAnchor` 为三态联合。trim 后分类：
 *   - 空 → invalid
 *   - `#` 开头：去 `#` 后 id 非空且不含嵌入 `#` → home-anchor；否则 invalid
 *   - 含 `#`（非首）：拆 `<guid>#<anchor>`，guid 合法 + anchor 非空且不含嵌入
 *     `#` → detail 带 anchor；否则 invalid
 *   - 不含 `#`：Guid 合法 → detail 顶部；否则 invalid
 */
export function parseTarget(raw: string): NavTarget {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { kind: 'invalid', raw };
  }
  if (trimmed.startsWith(MODULE_PREFIX)) {
    return parseModuleTarget(trimmed, raw);
  }
  if (trimmed.startsWith(HASH_PREFIX)) {
    const id = trimmed.slice(1);
    if (id === '' || id.includes(HASH_PREFIX)) {
      return { kind: 'invalid', raw };
    }
    return { kind: 'home-anchor', id, hash: `${HASH_PREFIX}${id}` };
  }
  const hashIndex = trimmed.indexOf(HASH_PREFIX);
  if (hashIndex > 0) {
    const sectionId = trimmed.slice(0, hashIndex);
    const anchor = trimmed.slice(hashIndex + 1);
    if (!isGuidWire(sectionId) || anchor === '' || anchor.includes(HASH_PREFIX)) {
      return { kind: 'invalid', raw };
    }
    return { kind: 'detail', sectionId, anchor };
  }
  if (isGuidWire(trimmed)) {
    return { kind: 'detail', sectionId: trimmed };
  }
  return { kind: 'invalid', raw };
}

function parseModuleTarget(trimmed: string, raw: string): NavTarget {
  const withoutPrefix = trimmed.slice(MODULE_PREFIX.length);
  const hashIndex = withoutPrefix.indexOf(HASH_PREFIX);
  const pathPart = hashIndex >= 0 ? withoutPrefix.slice(0, hashIndex) : withoutPrefix;
  const anchor = hashIndex >= 0 ? withoutPrefix.slice(hashIndex + 1) : undefined;
  const pathSegments = pathPart.split('/');
  const [routeName, moduleId, extra] = pathSegments;
  if (
    routeName === undefined
    || !isModuleRouteName(routeName)
    || extra !== undefined
    || (moduleId !== undefined && !isModuleId(moduleId))
    || (anchor !== undefined && (anchor === '' || anchor.includes(HASH_PREFIX)))
  ) {
    return { kind: 'invalid', raw };
  }
  return {
    kind: 'module',
    routeName,
    moduleId,
    anchor,
  };
}
